import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Loader2, Save } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Contract } from "../../types/contract";
import type {
  AiSuggestionDetail,
  AiSuggestionRow,
  ContentRiskLevel,
} from "../../types/aiSuggestion";
import { fetchAiSuggestionsByRiskLevel } from "../../services/aiSuggestionsService";
import { complete } from "../../services/reviewService";
import { ApiError } from "../../services/apiClient";
import {
  getCurrentUserId,
  getEffectiveAccess,
} from "../../services/authSessionService";
import { canCurrentUserCompleteGeneratedContractReview } from "../../utils/generatedContractPermission";
import {
  ALL_CONTENT_RISK_LEVELS,
  canActOnSuggestionRiskLevel,
  defaultViewContentRiskLevel,
  normalizeDecisionRoleCode,
} from "../../utils/decisionRoleRiskVisibility";
import {
  isContractReviewReadOnly,
  shouldShowSubmitForReviewOnSummary,
  shouldSuppressAiSuggestionsPanel,
} from "../../utils/contractReviewPhase";
import { stableSuggestionRowId } from "../../utils/aiSuggestionDisplay";
import {
  clearAllNavigationHighlights,
  navigateToSuggestionIndexes,
} from "../../services/officeService";
import { clauseLocateHintsFromSuggestion } from "../../utils/clauseLocateHints";
import {
  bindSharePointTargetingFromContract,
  saveDocumentPreferred,
} from "../../services/suggestionSharePointSyncService";
// import { navigateToSuggestionIndexes } from "../../services/officeService";
import { formatPillPrimaryCount } from "../../utils/riskStripDisplay";
import { RiskSummaryPill } from "../molecules/RiskSummaryPill";
import { SuggestionCard } from "../molecules/SuggestionCard";

const normalizeSuggestionItemId = (id: string): string =>
  id.trim().toLowerCase();

const normalizeReviewStatus = (raw: string | null | undefined): string =>
  (raw ?? "").trim().toLowerCase();

type ApprovedBucketProgress = {
  readonly approved: number;
  readonly total: number;
};

const fractionFromApprovedProgress = (
  sticky: ApprovedBucketProgress | undefined,
): string | null => {
  if (!sticky || sticky.total <= 0) {
    return null;
  }
  const { approved, total } = sticky;
  const safeApproved = Math.max(
    0,
    Math.min(Math.floor(approved), Math.floor(total)),
  );
  return `${safeApproved}/${Math.floor(total)}`;
};

type ReviewSummaryViewProps = {
  contract: Contract;
  onSuccess: () => void;
};

export const ReviewSummaryView = ({
  contract,
  onSuccess,
}: ReviewSummaryViewProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [saveDocumentMessage, setSaveDocumentMessage] = useState<{
    readonly type: "success" | "error";
    readonly text: string;
  } | null>(null);

  const effectiveAccess = useMemo(() => getEffectiveAccess(), []);

  const [contentRiskLevel, setContentRiskLevel] = useState<ContentRiskLevel>(
    defaultViewContentRiskLevel,
  );
  const [suggestions, setSuggestions] = useState<readonly AiSuggestionRow[]>(
    [],
  );
  const [redCount, setRedCount] = useState(0);
  const [amberCount, setAmberCount] = useState(0);
  const [greenCount, setGreenCount] = useState(0);
  const [redTotal, setRedTotal] = useState<number | null>(null);
  const [amberTotal, setAmberTotal] = useState<number | null>(null);
  const [greenTotal, setGreenTotal] = useState<number | null>(null);
  const [suggestionsLoad, setSuggestionsLoad] = useState<
    "idle" | "loading" | "error"
  >("loading");
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [activeClauseId, setActiveClauseId] = useState<string | null>(null);
  /** Per risk bucket: approved/total from last idle load of that bucket’s list (sticky when switching pills). */
  const [approvedProgressByLevel, setApprovedProgressByLevel] = useState<
    Partial<Record<ContentRiskLevel, ApprovedBucketProgress>>
  >({});

  const templateAssignReviewerFlow =
    contract.status === "Assign to Reviewer" ||
    contract.sourceKind === "template_generated";

  const isReadOnly = isContractReviewReadOnly(contract);
  const suppressAiSuggestionsPanel =
    shouldSuppressAiSuggestionsPanel(contract) || templateAssignReviewerFlow;

  const openedFromContractList = Boolean(
    (location.state as { hostDocumentOpenedFromList?: boolean } | null)
      ?.hostDocumentOpenedFromList,
  );

  const showSubmitForReview =
    !isReadOnly &&
    !suppressAiSuggestionsPanel &&
    shouldShowSubmitForReviewOnSummary(contract, {
      openedFromContractList: openedFromContractList,
    });

  const isRedDecisionRole =
    normalizeDecisionRoleCode(effectiveAccess?.decision_role_code ?? null) ===
    "RED";

  const hasAssignedReviewer = (contract.reviewerUserId ?? "").trim().length > 0;

  const canCompleteReview = useMemo(() => {
    const allowedByReviewerAssignment =
      canCurrentUserCompleteGeneratedContractReview(
        contract,
        getCurrentUserId(),
      );
    return (
      allowedByReviewerAssignment && (hasAssignedReviewer || isRedDecisionRole)
    );
  }, [contract, hasAssignedReviewer, isRedDecisionRole]);

  const canShowCompleteButton = !isReadOnly && canCompleteReview;

  const showReviewerOnlyHint =
    !isReadOnly &&
    (contract.reviewerUserId ?? "").trim().length > 0 &&
    !canCurrentUserCompleteGeneratedContractReview(
      contract,
      getCurrentUserId(),
    );

  const showYellowGreenReviewerHint =
    !isReadOnly && !hasAssignedReviewer && !isRedDecisionRole;

  const completeFullWidth = !showSubmitForReview && canShowCompleteButton;

  const resetRiskBucketState = useCallback((level: ContentRiskLevel): void => {
    switch (level) {
      case "LegalDecision":
        setRedCount(0);
        setRedTotal(null);
        break;
      case "BusinessDecision":
        setAmberCount(0);
        setAmberTotal(null);
        break;
      case "ExecDecision":
        setGreenCount(0);
        setGreenTotal(null);
        break;
    }
    setApprovedProgressByLevel((prev) => {
      if (!(level in prev)) {
        return prev;
      }
      const next: Partial<Record<ContentRiskLevel, ApprovedBucketProgress>> = {
        ...prev,
      };
      delete next[level];
      return next;
    });
  }, []);

  const loadSuggestions = useCallback(
    async (mode: "full" | "soft" = "full") => {
      const soft = mode === "soft";
      const requestedLevel = contentRiskLevel;
      if (suppressAiSuggestionsPanel) {
        if (!soft) {
          setSuggestionsLoad("idle");
          setSuggestionsError(null);
          setSuggestions([]);
          setActiveClauseId(null);
        }
        return;
      }

      if (!soft) {
        setSuggestionsLoad("loading");
        setSuggestionsError(null);
        setSuggestions([]);
        setActiveClauseId(null);
      }

      try {
        const result = await fetchAiSuggestionsByRiskLevel(
          contract.id,
          requestedLevel,
        );
        setSuggestions(result.suggestions);
        setRedCount(result.redCount);
        setAmberCount(result.amberCount);
        setGreenCount(result.greenCount);
        setRedTotal(result.redTotal);
        setAmberTotal(result.amberTotal);
        setGreenTotal(result.greenTotal);
        setSuggestionsLoad("idle");
      } catch (err) {
        resetRiskBucketState(requestedLevel);
        if (!soft) {
          setSuggestionsLoad("error");
          const message =
            err instanceof Error && err.message
              ? err.message
              : "Unable to load AI suggestions. Try again later.";
          setSuggestionsError(message);
          setActiveClauseId(null);
        }
      }
    },
    [
      contract.id,
      contentRiskLevel,
      resetRiskBucketState,
      suppressAiSuggestionsPanel,
    ],
  );

  const patchSuggestionPending = useCallback(
    (suggestionItemId: string): void => {
      const target = normalizeSuggestionItemId(suggestionItemId);
      if (!target) {
        return;
      }
      setSuggestions((prev) =>
        prev.map((row) =>
          normalizeSuggestionItemId(row.suggestion_item_id) === target
            ? ({
                ...row,
                review_status: "pending",
                approved_text: null,
                reviewed_at: null,
                reviewed_by: null,
              } satisfies AiSuggestionDetail)
            : row,
        ),
      );
    },
    [],
  );

  const handleSuggestionUndoComplete = useCallback(
    async (suggestionItemId: string) => {
      await loadSuggestions("soft");
      patchSuggestionPending(suggestionItemId);
    },
    [loadSuggestions, patchSuggestionPending],
  );

  const handleUndoReconcile = useCallback(() => {
    void loadSuggestions("full");
  }, [loadSuggestions]);

  const handleSuggestionDocumentActivate = useCallback(
    async (suggestion: AiSuggestionRow, listIndex: number) => {
      const rowId = stableSuggestionRowId(suggestion, listIndex);
      setActiveClauseId(rowId);
      const result = await navigateToSuggestionIndexes(
        suggestion.section_index,
        suggestion.content_index,
        {
          clauseTextHints: clauseLocateHintsFromSuggestion(suggestion),
        },
      );
      if (!result.ok) {
        globalThis.alert(result.message);
      }
    },
    [],
  );
  // const handleSuggestionDocumentActivate = useCallback(
  //   async (suggestion: AiSuggestionRow, listIndex: number) => {
  //     const rowId = stableSuggestionRowId(suggestion, listIndex);
  //     setActiveClauseId(rowId);
  //     const result = await navigateToSuggestionIndexes(
  //       suggestion.section_index,
  //       suggestion.content_index,
  //     );
  //     if (!result.ok) {
  //       globalThis.alert(result.message);
  //     }
  //   },
  //   [],
  // );

  // Refetch when returning from suggestion detail (`navigate` updates `location.key`) so statuses stay in sync.
  useEffect(() => {
    void loadSuggestions();
  }, [loadSuggestions, location.key]);

  useEffect(() => {
    setActiveClauseId(null);
    void clearAllNavigationHighlights();
  }, [contract.id, location.key]);

  useEffect(() => {
    bindSharePointTargetingFromContract(contract);
  }, [contract]);

  useEffect(() => {
    setApprovedProgressByLevel({});
  }, [contract.id]);

  useEffect(() => {
    setSaveDocumentMessage(null);
  }, [contract.id]);

  /** Strip totals from GET /generated-contracts (`ai_suggestion_counts_by_risk`): Red=Legal, Amber=Business, Green=Exec. */
  const countsByRisk = contract.aiSuggestionCountsByRisk;
  const displayRedCount =
    countsByRisk != null ? countsByRisk.LegalDecision : redCount;
  const displayAmberCount =
    countsByRisk != null ? countsByRisk.BusinessDecision : amberCount;
  const displayGreenCount =
    countsByRisk != null ? countsByRisk.ExecDecision : greenCount;
  const displayRedTotal = countsByRisk != null ? null : redTotal;
  const displayAmberTotal = countsByRisk != null ? null : amberTotal;
  const displayGreenTotal = countsByRisk != null ? null : greenTotal;

  /** When a bucket’s list finishes loading, store `approved/total` for its pill (kept when switching to other buckets). */
  useEffect(() => {
    if (suggestionsLoad !== "idle") {
      return;
    }
    const level = contentRiskLevel;
    setApprovedProgressByLevel((prev) => {
      const next: Partial<Record<ContentRiskLevel, ApprovedBucketProgress>> = {
        ...prev,
      };
      if (suggestions.length > 0) {
        const approved = suggestions.filter(
          (s) => normalizeReviewStatus(s.review_status) === "approved",
        ).length;
        next[level] = { approved, total: suggestions.length };
      } else {
        delete next[level];
      }
      return next;
    });
  }, [contentRiskLevel, suggestions, suggestionsLoad]);

  const pillRedPrimary = useMemo(() => {
    return (
      fractionFromApprovedProgress(approvedProgressByLevel.LegalDecision) ??
      formatPillPrimaryCount(displayRedCount, displayRedTotal)
    );
  }, [approvedProgressByLevel.LegalDecision, displayRedCount, displayRedTotal]);

  const pillAmberPrimary = useMemo(() => {
    return (
      fractionFromApprovedProgress(approvedProgressByLevel.BusinessDecision) ??
      formatPillPrimaryCount(displayAmberCount, displayAmberTotal)
    );
  }, [
    approvedProgressByLevel.BusinessDecision,
    displayAmberCount,
    displayAmberTotal,
  ]);

  const pillGreenPrimary = useMemo(() => {
    return (
      fractionFromApprovedProgress(approvedProgressByLevel.ExecDecision) ??
      formatPillPrimaryCount(displayGreenCount, displayGreenTotal)
    );
  }, [
    approvedProgressByLevel.ExecDecision,
    displayGreenCount,
    displayGreenTotal,
  ]);

  const handleSaveDocument = useCallback(async () => {
    if (isSavingDocument || isSubmitting) {
      return;
    }
    setIsSavingDocument(true);
    setSaveDocumentMessage(null);
    try {
      await saveDocumentPreferred({
        graphDriveId: contract.graphDriveId,
        graphItemId: contract.graphItemId,
      });
      setSaveDocumentMessage({
        type: "success",
        text: "Document saved successfully.",
      });
    } catch (err) {
      const detail =
        err instanceof Error ? err.message : "Unable to save the document.";
      setSaveDocumentMessage({
        type: "error",
        text: detail.slice(0, 300),
      });
    } finally {
      setIsSavingDocument(false);
    }
  }, [
    contract.graphDriveId,
    contract.graphItemId,
    isSavingDocument,
    isSubmitting,
  ]);

  const handleComplete = async () => {
    if (!canShowCompleteButton) {
      return;
    }
    setIsSubmitting(true);
    try {
      await complete(contract.id);
      onSuccess();
    } catch (err) {
      const message =
        err instanceof ApiError && err.message
          ? err.message
          : err instanceof Error && err.message
            ? err.message
            : "Unable to complete review. Try again later.";
      globalThis.alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="min-w-0 flex-1 text-sm font-semibold text-gray-900">
            Review Summary &amp; Recommendations
          </h2>
          <button
            type="button"
            onClick={() => void handleSaveDocument()}
            disabled={isSavingDocument || isSubmitting}
            title="Save the open Word document"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs font-medium text-foreground transition-all duration-200 ease-in-out hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSavingDocument ? (
              <Loader2
                className="h-3.5 w-3.5 animate-spin"
                aria-hidden
                strokeWidth={2}
              />
            ) : (
              <Save className="h-3.5 w-3.5" aria-hidden strokeWidth={2} />
            )}
            {isSavingDocument ? "Saving…" : "Save Document"}
          </button>
        </div>
        {saveDocumentMessage ? (
          <p
            className={`mt-2 text-xs ${
              saveDocumentMessage.type === "success"
                ? "text-green-700"
                : "text-destructive"
            }`}
            role={saveDocumentMessage.type === "success" ? "status" : "alert"}
          >
            {saveDocumentMessage.text}
          </p>
        ) : null}
      </div>
      {!suppressAiSuggestionsPanel ? (
        <div>
          <div className="border-b border-gray-200 bg-gray-100 px-4 py-2">
            {/* <RiskCountsLine
          redCount={redCount}
          amberCount={amberCount}
          greenCount={greenCount}
        /> */}
            <RiskSummaryPill
              activeLevel={contentRiskLevel}
              onSelectLevel={setContentRiskLevel}
              pillRedPrimary={pillRedPrimary}
              pillAmberPrimary={pillAmberPrimary}
              pillGreenPrimary={pillGreenPrimary}
              isLoading={suggestionsLoad === "loading"}
              allowedLevels={ALL_CONTENT_RISK_LEVELS}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {suggestionsLoad === "loading" ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Loading suggestions…
              </p>
            ) : null}
            {suggestionsLoad === "error" && suggestionsError ? (
              <p
                className="px-4 py-6 text-center text-sm text-destructive"
                role="alert"
              >
                {suggestionsError}
              </p>
            ) : null}
            {suggestionsLoad !== "loading" &&
            suggestionsLoad !== "error" &&
            suggestions.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                No AI suggestions for this filter.
              </p>
            ) : null}

            {suggestions.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {suggestions.map((s, index) => {
                  const rowId = stableSuggestionRowId(s, index);
                  return (
                    <div
                      key={rowId}
                      className="relative cursor-pointer transition-all hover:bg-gray-50"
                    >
                      <SuggestionCard
                        key={rowId}
                        contractId={contract.id}
                        contentRiskLevel={contentRiskLevel}
                        suggestion={s}
                        lineClampClassName="line-clamp-1"
                        readOnly={isReadOnly}
                        canActOnSuggestion={canActOnSuggestionRiskLevel(
                          effectiveAccess,
                          s.content_risk_level,
                        )}
                        sharePointTarget={{
                          graphDriveId: contract.graphDriveId,
                          graphItemId: contract.graphItemId,
                        }}
                        onUndoComplete={handleSuggestionUndoComplete}
                        onUndoReconcile={handleUndoReconcile}
                        isActive={activeClauseId === rowId}
                        onDocumentActivate={() =>
                          void handleSuggestionDocumentActivate(s, index)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
        {isReadOnly ? (
          <p
            className="mb-2 text-sm text-muted-foreground transition-all duration-200 ease-in-out"
            role="status"
          >
            This contract review is complete. Suggestions above are view-only.
          </p>
        ) : null}
        {showReviewerOnlyHint ? (
          <p
            className="mb-2 text-sm text-muted-foreground transition-all duration-200 ease-in-out"
            role="status"
          >
            Only the assigned reviewer can complete this review.
          </p>
        ) : null}
        {showYellowGreenReviewerHint ? (
          <p
            className="mb-2 text-sm text-muted-foreground transition-all duration-200 ease-in-out"
            role="status"
          >
            Complete is available only for Legal Decision (Red) role reviewers.
          </p>
        ) : null}
        <div className="flex gap-2">
          {showSubmitForReview ? (
            <button
              type="button"
              onClick={() =>
                navigate(`/contractiq/contracts/${contract.id}/review/submit`)
              }
              disabled={isSubmitting}
              className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-all duration-200 ease-in-out hover:bg-blue-700 disabled:opacity-50"
            >
              <Check className="h-4 w-4" aria-hidden />
              Submit for Review
            </button>
          ) : null}
          {canShowCompleteButton ? (
            <button
              type="button"
              onClick={() => void handleComplete()}
              disabled={isSubmitting}
              aria-disabled={isSubmitting}
              className={`h-9 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-all duration-200 ease-in-out hover:bg-gray-100 disabled:opacity-50 ${
                completeFullWidth ? "w-full" : "flex-1"
              }`}
            >
              Complete
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
